import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, useScroll } from "framer-motion";
import { ShieldCheck, ScanSearch, FileCheck2 } from "lucide-react";
import { IMAGES } from "@/data/products";
import { EASE } from "@/components/site/Motion";

const Chip = ({ icon: Icon, label, sub, className, z, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    style={{ transform: `translateZ(${z}px)` }}
    className={`absolute flex items-center gap-3 rounded-2xl bg-white/85 backdrop-blur-xl border border-white px-4 py-3 shadow-[0_24px_50px_-20px_rgba(7,11,46,0.45)] ${className}`}
  >
    <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#070B2E] text-[#7FE3EA]"><Icon className="w-4 h-4" /></span>
    <span>
      <span className="block text-sm font-semibold text-[#070B2E]">{label}</span>
      <span className="block font-mono text-[11px] text-[#4A4F6E]">{sub}</span>
    </span>
  </motion.div>
);

export const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [9, -9]), { stiffness: 110, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-12, 12]), { stiffness: 110, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${sx} ${sy}, rgba(255,255,255,0.28), transparent 60%)`;
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 700], [0, -90]);
  const imgScale = useTransform(scrollY, [0, 700], [1.08, 1.2]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.div ref={ref} style={{ y: py }} onMouseMove={onMove} onMouseLeave={onLeave} className="relative [perspective:1400px]" data-testid="hero-visual">
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
      >
        <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
          <div className="clip-notch relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden rounded-[28px] bg-[#070B2E]">
            <motion.img src={IMAGES.dashboard} alt="Analys av byråns uppdrag på skärm" style={{ scale: imgScale }} className="absolute inset-0 w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070B2E] via-[#070B2E]/30 to-transparent" />
            <motion.div className="absolute inset-0 mix-blend-soft-light" style={{ background: spotlight }} />
            <div className="absolute left-6 right-6 bottom-6 text-white">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#7FE3EA]">Byråöversikt</p>
              <p className="font-display text-2xl sm:text-3xl font-semibold mt-2 leading-tight">Alla kunder. Alla uppdrag.<br />Full kontroll.</p>
            </div>
          </div>
          <Chip icon={ShieldCheck} label="KYC upprättad" sub="Årlig översyn: klar" z={80} delay={1.0} className="-left-4 sm:-left-10 top-10" />
          <Chip icon={FileCheck2} label="Checklista bokslut" sub="12 av 12 moment" z={60} delay={1.15} className="-right-3 sm:-right-8 top-[42%]" />
          <Chip icon={ScanSearch} label="Recon.ai" sub="0 avvikelser hittade" z={100} delay={1.3} className="left-8 -bottom-6 hidden sm:flex" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};
